import moment from 'moment';

const START_OF_WORKDAY = 9;
const END_OF_WORKDAY = 17;
const DEFAULT_EXCLUDE_OPTIONS = {
  type: 0,
  workStart: START_OF_WORKDAY,
  workEnd: END_OF_WORKDAY
};

const MINUTES_PER_DAY = 1440;

function getWorkMinutesPerDay(excludeWeekendOpts, useWorkingHoursInCycleTimeDays = false) {
  if (!useWorkingHoursInCycleTimeDays) {
    return MINUTES_PER_DAY;
  }
  if (excludeWeekendOpts && +excludeWeekendOpts.type > 1
      && excludeWeekendOpts.workStart && excludeWeekendOpts.workEnd) {
    return moment(excludeWeekendOpts.workEnd, 'HH:mm')
      .diff(moment(excludeWeekendOpts.workStart, 'HH:mm'), 'minutes');
  }
  return MINUTES_PER_DAY;
}

function getDisplayMinutesPerDay(excludeWeekendOpts, useWorkingHoursInCycleTimeDays = false) {
  return getWorkMinutesPerDay(excludeWeekendOpts, useWorkingHoursInCycleTimeDays);
}

const WORK_DAYS_PER_WEEK = 5;
const WORK_DAYS_PER_MONTH = 20;

function formatWorkDuration(totalMinutes, excludeWeekendOpts, timePrecision, useWorkingHoursInCycleTimeDays = false, itemDate = null) {
  if (!useWorkingHoursInCycleTimeDays) {
    return moment.duration(totalMinutes, 'minutes').format(timePrecision, { trim: 'mid both' });
  }

  const workMinutesPerDay = getWorkMinutesPerDay(excludeWeekendOpts, useWorkingHoursInCycleTimeDays);
  const hasDaysOnly = timePrecision && timePrecision.includes('d[d]') && !timePrecision.includes('h[h]') && !timePrecision.includes('m[m]');
  let totalWorkDays = hasDaysOnly ? Math.ceil(totalMinutes / workMinutesPerDay) : Math.floor(totalMinutes / workMinutesPerDay);
  const remainingAfterDays = totalMinutes % workMinutesPerDay;
  const hours = Math.floor(remainingAfterDays / 60);
  const minutes = Math.floor(remainingAfterDays % 60);

  const hasWeeks = timePrecision && timePrecision.includes('w[w]');
  const hasMonths = timePrecision && timePrecision.includes('M[m]');

  let months = 0;
  let weeks = 0;
  let days = totalWorkDays;

  if (hasMonths) {
    let workDaysPerMonth;
    if (itemDate) {
      const date = new Date(itemDate);
      workDaysPerMonth = getWorkDaysInMonth(date.getFullYear(), date.getMonth(), excludeWeekendOpts?.holidays);
    } else {
      workDaysPerMonth = WORK_DAYS_PER_MONTH;
    }
    months = Math.floor(totalWorkDays / workDaysPerMonth);
    totalWorkDays = totalWorkDays % workDaysPerMonth;
  }

  if (hasWeeks) {
    weeks = Math.floor(totalWorkDays / WORK_DAYS_PER_WEEK);
    days = totalWorkDays % WORK_DAYS_PER_WEEK;
  } else {
    days = totalWorkDays;
  }

  if (timePrecision) {
    const hasDays = timePrecision.includes('d[d]');
    const hasHours = timePrecision.includes('h[h]');
    const hasMinutes = timePrecision.includes('m[m]');
    let displayHours = hours;
    let displayDays = days;
    let displayMinutes = minutes;

    if (!hasDays && hasHours) {
      const daysAsMinutes = days * workMinutesPerDay;
      const totalRemainingMinutes = daysAsMinutes + (hours * 60) + minutes;
      displayHours = hasMinutes ? Math.floor(totalRemainingMinutes / 60) : Math.ceil(totalRemainingMinutes / 60);
      displayMinutes = totalRemainingMinutes % 60;
      displayDays = 0;
    }

    const parts = [];
    if (hasMonths && months > 0) parts.push(`${months}m`);
    if (hasWeeks && weeks > 0) parts.push(`${weeks}w`);
    if (hasDays && displayDays > 0) parts.push(`${displayDays}d`);
    if (hasHours && displayHours > 0) parts.push(`${displayHours}h`);
    if (timePrecision.includes('m[m]') && displayMinutes > 0) parts.push(`${displayMinutes}m`);
    return parts.length > 0 ? parts.join(' ') : '0m';
  }

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '0m';
}

function chunkRange(start, end) {
  const roundStart = start.endOfDay();
  const roundEnd = end.startOfDay();
  const diff = roundEnd < roundStart ? 0 : roundEnd.diff(roundStart);
  if (!diff) return [{ start, end }];

  const arr = [];
  if (+start !== +roundStart) arr.push({ start, end: roundStart });
  if (diff > 1) {
    for (let i = roundStart; i.isBefore(roundEnd); i = i.addDays(1)) {
      const d = i.addDays(1);
      arr.push({ start: d.startOfDay(), end: d });
    }
    arr.pop();
  }
  if (+roundEnd !== +end) arr.push({ start: roundEnd, end });
  return arr;
}

function getWorkHours(day, workStart, workEnd) {
  const startOfDay = new Date(day.start);
  const startTime = new moment(workStart, 'HH:mm');
  startOfDay.setHours(startTime.get('hour'), startTime.get('minute'), 0, 0);

  const endOfDay = new Date(day.end);
  const endTime = new moment(workEnd, 'HH:mm');
  endOfDay.setHours(endTime.get('hour'), endTime.get('minute'), 0, 0);

  if (day.start.isSameOrAfter(endOfDay)) {
    return 0;
  }

  if (day.start.isSameOrAfter(startOfDay) && day.end.isSameOrBefore(endOfDay)) {
    return day.end.getTime() - day.start.getTime();
  } else if (day.start.isBefore(startOfDay) && day.end.isSameOrBefore(endOfDay)) {
    return day.end.getTime() - startOfDay.getTime();
  } else if (day.start.isSameOrAfter(startOfDay) && day.end.isAfter(endOfDay)) {
    return endOfDay.getTime() - day.start.getTime();
  } else {
    return endOfDay.getTime() - startOfDay.getTime();
  }
}

function checkForHoliday(date, holidays = []) {
  for (let i = 0; i < holidays.length; i++) {
    if (date.isSameDay(holidays[i])) {
      return true;
    }
  }

  return false;
}

function getWorkDaysInMonth(year, month, holidays = []) {
  let workDays = 0;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    if (!date.isWeekend() && !checkForHoliday(date, holidays)) {
      workDays++;
    }
  }
  return workDays;
}

Date.prototype.substract = function(days) {
  const date = new Date(this.valueOf());

  date.setDate(date.getDate() - days);
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.addDays = function(days) {
  const date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

Date.prototype.startOfDay = function() {
  const date = new Date(this.valueOf());
  date.setHours(0, 0, 0, 0);
  return date;
};

Date.prototype.endOfDay = function() {
  const date = new Date(this.valueOf());
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.endOfWeek = function(weekStartsOn = 1, numOfWeeks = 1) {
  const date = new Date(this.valueOf());
  const day = date.getDay();
  const diff = (day < weekStartsOn ? -7 : 0) + 6 - (day - weekStartsOn) + (numOfWeeks > 1 ? (numOfWeeks - 1) * 7 : 0);

  date.setDate(date.getDate() + diff);
  date.setHours(23, 59, 59, 999);

  return date;
};

Date.prototype.endOfMonth = function() {
  const date = new Date(this.valueOf());
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.startOfMonth = function() {
  const date = new Date(this.valueOf());
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month, 1);
  date.setHours(23, 59, 59, 999);

  return date;
};

Date.prototype.endOf = function(unit, numOfWeeks = 1) {
  switch (unit) {
    case 'week': {
      return new Date(this.valueOf()).endOfWeek(1, numOfWeeks);
    }
    case 'month': {
      return new Date(this.valueOf()).endOfMonth();
    }
    default: {
      return new Date(this.valueOf()).endOfDay();
    }
  }
};

Date.prototype.inBetween = function(start, from) {
  const day = new Date(this.valueOf()).getTime();
  const startDay = new Date(start).getTime();
  const fromDay = new Date(from).getTime();

  return day >= startDay && day <= fromDay;
};

Date.prototype.isSameOrAfter = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day >= otherDay;
};

Date.prototype.isAfter = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day > otherDay;
};

Date.prototype.isSameOrBefore = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day <= otherDay;
};

Date.prototype.isBefore = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day < otherDay;
};

Date.prototype.isSameDay = function(other) {
  const day = new Date(this.valueOf());
  const otherDay = new Date(other);

  return (
    day.getDate() === otherDay.getDate() &&
    day.getMonth() === otherDay.getMonth() &&
    day.getFullYear() === otherDay.getFullYear()
  );
};

Date.prototype.isWeekend = function() {
  return (new Date(this.valueOf()).getDay() || 7) > 5;
};

Date.prototype.diff = function(other, unit, excludeOpts = DEFAULT_EXCLUDE_OPTIONS, absolute = true) {
  let day = new Date(this.valueOf());
  let otherDay = new Date(other);

  let timeDiff;
  if (absolute) {
    timeDiff = Math.abs(otherDay.getTime() - day.getTime());
  } else {
    timeDiff = otherDay.getTime() - day.getTime();
    if (timeDiff < 0) {
      timeDiff = 60000;
    }
  }

  let workTimeDiff = timeDiff;
  if (+excludeOpts.type > 0) {
    //Check if second date is before first date to switch
    if (day.isBefore(otherDay)) {
      day = new Date(other);
      otherDay = new Date(this.valueOf());
    }

    if (+excludeOpts.type === 1) {
      const days = chunkRange(otherDay, day);
      for (let i = 0; i < days.length; i++) {
        const d = days[i];
        if (d.start.isWeekend() || checkForHoliday(d.start, excludeOpts.holidays)) {
          workTimeDiff -= d.end.getTime() - d.start.getTime();
        }
      }
    } else {
      const days = chunkRange(otherDay, day);
      workTimeDiff = 0;

      for (let i = 0; i < days.length; i++) {
        const d = days[i];
        if (d.start.isWeekend() || checkForHoliday(d.start, excludeOpts.holidays)) {
          continue;
        }

        if (days.length > 1) {
          if (i === 0 || i === days.length - 1) {
            workTimeDiff += getWorkHours(d, excludeOpts.workStart, excludeOpts.workEnd);
          } else {
            const workMinutes = moment(excludeOpts.workEnd, 'HH:mm').diff(
              moment(excludeOpts.workStart, 'HH:mm'),
              'milliseconds'
            );
            workTimeDiff += workMinutes;
          }
        } else {
          workTimeDiff = getWorkHours(d, excludeOpts.workStart, excludeOpts.workEnd);
        }
      }
    }

    if (workTimeDiff < 0) {
      workTimeDiff = 0;
    }
  }

  switch (unit) {
    case 'minutes': {
      return Math.ceil(workTimeDiff / (1000 * 60));
    }
    case 'hours': {
      return Math.ceil(workTimeDiff / (1000 * 3600));
    }
    case 'days': {
      return Math.ceil(workTimeDiff / (1000 * 3600 * 24));
    }
    default: {
      return workTimeDiff;
    }
  }
};

function formatDateSpan(first, last) {
  if (first.isSame(last, 'day')) {
    return last.format('D MMM YYYY');
  }
  if (first.isSame(last, 'month')) {
    return `${first.format('D')} to ${last.format('D MMM YYYY')}`;
  }
  if (first.isSame(last, 'year')) {
    return `${first.format('D MMM')} to ${last.format('D MMM YYYY')}`;
  }
  return `${first.format('D MMM YYYY')} to ${last.format('D MMM YYYY')}`;
}

function createHighchartFormats(format) {
  const obj = {};

  switch (format) {
    case 'DD MMM YYYY': {
      obj.day = momentToStrftimeFormat('DD MMM');
      obj.week = momentToStrftimeFormat('DD MMM');
      obj.month = momentToStrftimeFormat('MMM YYYY');
      break;
    }
    case 'DD-MM-YYYY': {
      obj.day = momentToStrftimeFormat('DD-MM');
      obj.week = momentToStrftimeFormat('DD-MM');
      obj.month = momentToStrftimeFormat('MM-YYYY');
      break;
    }
    case 'DD/MM/YYYY': {
      obj.day = momentToStrftimeFormat('DD/MM');
      obj.week = momentToStrftimeFormat('DD/MM');
      obj.month = momentToStrftimeFormat('MM/YYYY');
      break;
    }
    case 'MMM DD, YYYY': {
      obj.day = momentToStrftimeFormat('MMM DD');
      obj.week = momentToStrftimeFormat('MMM DD');
      obj.month = momentToStrftimeFormat('MMM YYYY');
      break;
    }
    case 'MM-DD-YYYY': {
      obj.day = momentToStrftimeFormat('MM-DD');
      obj.week = momentToStrftimeFormat('MM-DD');
      obj.month = momentToStrftimeFormat('MM-YYYY');
      break;
    }
    case 'MM/DD/YYYY': {
      obj.day = momentToStrftimeFormat('MM/DD');
      obj.week = momentToStrftimeFormat('MM/DD');
      obj.month = momentToStrftimeFormat('MM/YYYY');
      break;
    }
    case 'YYYY-MM-DD': {
      obj.day = momentToStrftimeFormat('MM-DD');
      obj.week = momentToStrftimeFormat('MM-DD');
      obj.month = momentToStrftimeFormat('YYYY-MM');
      break;
    }
    case 'YYYY/MM/DD': {
      obj.day = momentToStrftimeFormat('MM/DD');
      obj.week = momentToStrftimeFormat('MM/DD');
      obj.month = momentToStrftimeFormat('YYYY/MM');
      break;
    }
  }

  return {
    ...obj,
    // the month alone, for a date axis too narrow for two "MMM YYYY" labels
    monthShort: '%b',
    // Relies on Highcharts 9.3 drawing the first format in the list that fits
    monthShortYear: { main: '%b %Y', list: ['%b %Y', '%b \'%y', '%b'] },
    millisecond: obj.day,
    second: obj.day,
    minute: obj.day,
    hour: obj.day
  };
}

function getFirstOfMonth(selectedDate) {
  const date = new Date(selectedDate);
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month, 1);
  date.setHours(23, 59, 59, 999);

  return date;
}

function momentToStrftimeFormat(format) {
  const conversions = {
    DD: '%d',
    YYYY: '%Y',
    MM: '%m',
    MMM: '%b'
  };

  return format.replace(/[A-Za-z]+/g, match => {
    return conversions[match] || match;
  });
}

function getDays(from, to) {
  if (moment(to).isAfter()) {
    to = moment.now();
  }

  return moment(to).diff(moment(from), 'days') + 1;
}

function generateDateRangeArray(from, to) {
  const days = getDays(from, to);
  const arr = [];
  for (let i = 0; i < days; i++) {
    arr.push({
      date: new Date(from).addDays(i)
    });
  }

  return arr;
}

function generateWeeksRangeArray(from, to) {
  from = new Date(from).setHours(0, 0, 0);
  to = new Date(to).setHours(23, 59, 59);

  const days = getDays(from, to);
  const arr = [];
  for (let i = 0; i < days; ) {
    arr.push({
      from: new Date(from).addDays(i),
      to: new Date(from).addDays(i + 13).setHours(23, 59, 59)
    });

    i += 14;
  }

  return arr;
}

function generateWeekRangeArray(from, to) {
  from = new Date(from).setHours(0, 0, 0);
  to = new Date(to).setHours(23, 59, 59);

  const days = getDays(from, to);
  const arr = [];
  for (let i = 0; i < days; ) {
    arr.push({
      from: new Date(from).addDays(i),
      to: new Date(from).addDays(i + 6).setHours(23, 59, 59)
    });

    i += 7;
  }

  return arr;
}

export { getWorkMinutesPerDay, getDisplayMinutesPerDay, MINUTES_PER_DAY, WORK_DAYS_PER_WEEK, WORK_DAYS_PER_MONTH, getWorkDaysInMonth, formatWorkDuration, formatDateSpan };

export default {
  getDays,
  createHighchartFormats,
  momentToStrftimeFormat,
  generateDateRangeArray,
  generateWeekRangeArray,
  generateWeeksRangeArray,
  getWorkMinutesPerDay,
  getDisplayMinutesPerDay,
  formatWorkDuration
};
